/**
 * SaveLoadPanel - UI panel for managing save games
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { SaveManager, SaveSlotInfo, DEFAULT_SAVE_SLOT } from '../save/SaveManager';
import { t } from '../systems/Localization';

export type SaveLoadMode = 'save' | 'load';

export class SaveLoadPanel extends UIPanel {
  private saveManager: SaveManager;
  private mode: SaveLoadMode = 'load';
  private saves: SaveSlotInfo[] = [];
  private hoveredSlot: number = -1;
  private selectedSlot: number = -1;
  private scrollOffset: number = 0;
  private maxVisibleSlots: number = 5;
  private slotHeight: number = 60;
  private editingName: boolean = false;
  private editingSlot: number = -1;
  private editBuffer: string = '';
  private isSaveAsMode: boolean = false; // true = save after edit, false = rename only

  // Drag and drop state for save reordering
  private draggingSaveSlot: number = -1;
  private saveDragStartY: number = 0;
  private isSaveDragging: boolean = false;
  private saveDragTargetSlot: number = -1;

  // Callbacks
  public onSave: ((slot: number, name: string) => void) | null = null;
  public onLoad: ((slot: number) => void) | null = null;
  public onDelete: ((slot: number) => void) | null = null;
  public onNewGame: (() => void) | null = null;

  constructor(saveManager: SaveManager) {
    super(0, 0, 400, 420);
    this.saveManager = saveManager;
    this.isDraggable = true;
    this.refreshSaves();
  }

  setMode(mode: SaveLoadMode): void {
    this.mode = mode;
    this.title = mode === 'save' ? t('save_title_save') : t('save_title_load');
    this.selectedSlot = -1;
    this.editingName = false;
    this.editingSlot = -1;
    this.isSaveAsMode = false;
    this.editBuffer = '';
    this.draggingSaveSlot = -1;
    this.isSaveDragging = false;
    this.saveDragTargetSlot = -1;
    this.refreshSaves();
  }

  show(): void {
    super.show();
    this.refreshSaves();
  }

  hide(): void {
    super.hide();
    // Reset drag state
    this.draggingSaveSlot = -1;
    this.isSaveDragging = false;
    this.saveDragTargetSlot = -1;
  }

  refreshSaves(): void {
    this.saves = this.saveManager.getAllSaveInfo();
  }

  protected onPanelClick(pos: Vector2): boolean {
    const contentY = this.bounds.y + this.titleBarHeight + 10;
    const listHeight = this.maxVisibleSlots * this.slotHeight;

    // Check if clicking on a save slot
    if (pos.y >= contentY && pos.y < contentY + listHeight) {
      const relY = pos.y - contentY + this.scrollOffset;
      const slotIndex = Math.floor(relY / this.slotHeight);

      if (slotIndex >= 0 && slotIndex < this.saves.length) {
        const slot = this.saves[slotIndex];
        if (!slot) return true;

        const slotY = contentY + slotIndex * this.slotHeight - this.scrollOffset;
        const buttonY = slotY + 35;

        // Check button clicks - buttons are rendered at x = this.bounds.x + 20
        const contentX = this.bounds.x + 20;
        const isDefaultSave = slot.slot === DEFAULT_SAVE_SLOT;
        if (pos.y >= buttonY && pos.y <= buttonY + 18) {
          if (slot.exists) {
            // Default save only has LOAD button (in load mode)
            if (isDefaultSave) {
              if (this.mode === 'load' && pos.x >= contentX && pos.x <= contentX + 50) {
                this.onLoad?.(slot.slot);
                this.hide();
              }
              return true;
            }

            // Load/Save button: x to x+50
            if (pos.x >= contentX && pos.x <= contentX + 50) {
              if (this.mode === 'load') {
                this.onLoad?.(slot.slot);
                this.hide();
              } else {
                // Save mode: start editing name first (Save As feature)
                this.startSaveAs(slot.slot);
              }
              return true;
            }
            // Rename button: x+55 to x+110
            if (pos.x >= contentX + 55 && pos.x <= contentX + 110) {
              this.startRenaming(slot.slot);
              return true;
            }
            // Export button: x+115 to x+165
            if (pos.x >= contentX + 115 && pos.x <= contentX + 165) {
              this.exportSaveToFile(slot.slot, slot.name);
              return true;
            }
            // Delete button: x+170 to x+220
            if (pos.x >= contentX + 170 && pos.x <= contentX + 220) {
              this.onDelete?.(slot.slot);
              this.refreshSaves();
              return true;
            }
          } else {
            // Empty slot - Import button or New Save
            if (this.mode === 'save') {
              // New save button: x to x+80
              if (pos.x >= contentX && pos.x <= contentX + 80) {
                this.startSaveAs(slot.slot);
                return true;
              }
            }
            // Import button: x+90 to x+150
            if (pos.x >= contentX + 90 && pos.x <= contentX + 150) {
              this.importSaveFromFile(slot.slot);
              return true;
            }
          }
        }

        this.selectedSlot = slot.slot;
        return true;
      }
    }

    // Check New Game button (in load mode)
    if (this.mode === 'load') {
      const newGameY = this.bounds.y + this.bounds.height - 50;
      if (pos.x >= this.bounds.x + 20 && pos.x <= this.bounds.x + 120 &&
          pos.y >= newGameY && pos.y <= newGameY + 35) {
        this.onNewGame?.();
        this.hide();
        return true;
      }
    }

    return true;
  }

  handleMouseDown(pos: Vector2): boolean {
    if (!this.isVisible || !this.containsPoint(pos)) return false;

    // Always let base class handle title bar (for panel dragging)
    if (pos.y < this.bounds.y + this.titleBarHeight) {
      return super.handleMouseDown(pos);
    }

    // Check if clicking on a save slot to start dragging
    const contentY = this.bounds.y + this.titleBarHeight + 10;
    const listHeight = this.maxVisibleSlots * this.slotHeight;

    if (pos.y >= contentY && pos.y < contentY + listHeight) {
      const relY = pos.y - contentY + this.scrollOffset;
      const slotIndex = Math.floor(relY / this.slotHeight);

      if (slotIndex >= 0 && slotIndex < this.saves.length) {
        const save = this.saves[slotIndex];
        // Don't allow dragging demo save or empty slots
        if (save && save.exists && save.slot !== DEFAULT_SAVE_SLOT) {
          this.draggingSaveSlot = slotIndex;
          this.saveDragStartY = pos.y;
          this.isSaveDragging = false;
          return true;
        }
      }
    }

    // Let base class handle other clicks
    return super.handleMouseDown(pos);
  }

  handleMouseUp(pos: Vector2): boolean {
    // Let base class handle panel dragging
    if (super.handleMouseUp(pos)) {
      return true;
    }

    // Handle drop for save slot reordering
    if (this.isSaveDragging && this.draggingSaveSlot >= 0 && this.saveDragTargetSlot >= 0) {
      const sourceSlot = this.saves[this.draggingSaveSlot];
      const targetSlot = this.saves[this.saveDragTargetSlot];

      if (sourceSlot && targetSlot && sourceSlot.slot !== targetSlot.slot) {
        // Swap the saves
        this.saveManager.swapSaves(sourceSlot.slot, targetSlot.slot);
        this.refreshSaves();
      }
    }

    this.draggingSaveSlot = -1;
    this.isSaveDragging = false;
    this.saveDragTargetSlot = -1;

    return false;
  }

  handleMouseMove(pos: Vector2): boolean {
    const panelDragging = super.handleMouseMove(pos);

    // Check if we should start dragging a save slot
    if (this.draggingSaveSlot >= 0 && !this.isSaveDragging) {
      const dragThreshold = 5;
      if (Math.abs(pos.y - this.saveDragStartY) > dragThreshold) {
        this.isSaveDragging = true;
      }
    }

    // Update drag target slot
    if (this.isSaveDragging) {
      const contentY = this.bounds.y + this.titleBarHeight + 10;
      const relY = pos.y - contentY + this.scrollOffset;
      const slotIndex = Math.floor(relY / this.slotHeight);

      if (slotIndex >= 0 && slotIndex < this.saves.length && slotIndex !== this.draggingSaveSlot) {
        const targetSave = this.saves[slotIndex];
        // Can only drop on non-demo slots
        if (targetSave && targetSave.slot !== DEFAULT_SAVE_SLOT) {
          this.saveDragTargetSlot = slotIndex;
        } else {
          this.saveDragTargetSlot = -1;
        }
      } else {
        this.saveDragTargetSlot = -1;
      }
    }

    if (this.isVisible && this.containsPoint(pos)) {
      const contentY = this.bounds.y + this.titleBarHeight + 10;
      const relY = pos.y - contentY + this.scrollOffset;
      const slotIndex = Math.floor(relY / this.slotHeight);

      if (slotIndex >= 0 && slotIndex < this.saves.length) {
        this.hoveredSlot = slotIndex;
      } else {
        this.hoveredSlot = -1;
      }
      return true;
    }

    this.hoveredSlot = -1;
    return panelDragging || this.isSaveDragging;
  }

  private startRenaming(slot: number): void {
    this.editingName = true;
    this.editingSlot = slot;
    this.isSaveAsMode = false;
    const save = this.saves.find(s => s.slot === slot);
    this.editBuffer = save?.name ?? '';
  }

  private startSaveAs(slot: number): void {
    this.editingName = true;
    this.editingSlot = slot;
    this.isSaveAsMode = true;
    const save = this.saves.find(s => s.slot === slot);
    // For new saves, suggest a default name; for existing, use current name
    this.editBuffer = save?.exists ? save.name : `Save ${slot + 1}`;
  }

  /** Export save to downloadable file */
  private exportSaveToFile(slot: number, saveName: string): void {
    const json = this.saveManager.exportSave(slot);
    if (!json) {
      console.error('Failed to export save');
      return;
    }

    // Create a blob and download it
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${saveName.replace(/[^a-z0-9]/gi, '_')}_save.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Exported save "${saveName}" to file`);
  }

  /** Import save from file */
  private importSaveFromFile(slot: number): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const json = event.target?.result as string;
        if (json) {
          const success = this.saveManager.importSave(json, slot);
          if (success) {
            console.log(`Imported save to slot ${slot}`);
            this.refreshSaves();
          } else {
            console.error('Failed to import save - invalid file');
          }
        }
      };
      reader.readAsText(file);
    };

    input.click();
  }

  handleKeyDown(key: string): boolean {
    if (!this.editingName) return false;

    if (key === 'Enter') {
      if (this.isSaveAsMode) {
        // Save with the new name
        this.onSave?.(this.editingSlot, this.editBuffer);
        this.editingName = false;
        this.editingSlot = -1;
        this.isSaveAsMode = false;
        this.hide();
      } else {
        // Just rename
        this.saveManager.renameSave(this.editingSlot, this.editBuffer);
        this.editingName = false;
        this.editingSlot = -1;
        this.refreshSaves();
      }
      return true;
    }

    if (key === 'Escape') {
      this.editingName = false;
      this.editingSlot = -1;
      this.isSaveAsMode = false;
      return true;
    }

    if (key === 'Backspace') {
      this.editBuffer = this.editBuffer.slice(0, -1);
      return true;
    }

    if (key.length === 1 && this.editBuffer.length < 20) {
      this.editBuffer += key;
      return true;
    }

    return true;
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const contentX = this.bounds.x + 20;
    let contentY = this.bounds.y + this.titleBarHeight + 10;

    // Save slots
    for (let i = 0; i < Math.min(this.saves.length, this.maxVisibleSlots); i++) {
      const actualIndex = i + Math.floor(this.scrollOffset / this.slotHeight);
      const save = this.saves[actualIndex];
      if (!save) continue;

      const slotY = contentY + i * this.slotHeight;
      const isHovered = this.hoveredSlot === i;
      const isSelected = this.selectedSlot === save.slot;
      const isBeingDragged = this.draggingSaveSlot === actualIndex && this.isSaveDragging;
      const isDragTarget = this.saveDragTargetSlot === actualIndex;

      this.renderSlot(ctx, contentX, slotY, save, isHovered, isSelected, isBeingDragged, isDragTarget);
    }

    // Scroll indicators if needed
    if (this.saves.length > this.maxVisibleSlots) {
      ctx.fillStyle = '#888';
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      if (this.scrollOffset > 0) {
        ctx.fillText('▲ ' + t('save_scroll_up'), this.bounds.x + this.bounds.width / 2, contentY - 5);
      }
      const maxScroll = (this.saves.length - this.maxVisibleSlots) * this.slotHeight;
      if (this.scrollOffset < maxScroll) {
        ctx.fillText('▼ ' + t('save_scroll_down'), this.bounds.x + this.bounds.width / 2,
          contentY + this.maxVisibleSlots * this.slotHeight + 15);
      }
    }

    // New Game button (in load mode only)
    if (this.mode === 'load') {
      const newGameY = this.bounds.y + this.bounds.height - 50;
      ctx.fillStyle = '#3498db';
      ctx.fillRect(this.bounds.x + 20, newGameY, 100, 35);
      ctx.strokeStyle = '#5dade2';
      ctx.lineWidth = 2;
      ctx.strokeRect(this.bounds.x + 20, newGameY, 100, 35);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(t('save_btn_new_game'), this.bounds.x + 70, newGameY + 22);
    }
  }

  private renderSlot(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    save: SaveSlotInfo,
    isHovered: boolean,
    isSelected: boolean,
    isBeingDragged: boolean = false,
    isDragTarget: boolean = false
  ): void {
    const width = this.bounds.width - 40;
    const isDefaultSave = save.slot === DEFAULT_SAVE_SLOT;

    // Background - special gold/amber color for default save
    if (isBeingDragged) {
      ctx.fillStyle = 'rgba(78, 204, 163, 0.5)';
    } else if (isDragTarget) {
      ctx.fillStyle = 'rgba(52, 152, 219, 0.5)';
    } else if (isDefaultSave) {
      if (isSelected) {
        ctx.fillStyle = 'rgba(255, 193, 7, 0.4)';
      } else if (isHovered) {
        ctx.fillStyle = 'rgba(255, 193, 7, 0.25)';
      } else {
        ctx.fillStyle = 'rgba(80, 60, 20, 0.9)';
      }
    } else if (isSelected) {
      ctx.fillStyle = 'rgba(78, 204, 163, 0.3)';
    } else if (isHovered) {
      ctx.fillStyle = 'rgba(78, 204, 163, 0.15)';
    } else {
      ctx.fillStyle = 'rgba(50, 50, 70, 0.8)';
    }
    ctx.fillRect(x, y, width, this.slotHeight - 5);

    // Border - gold for default save, blue for drag target
    if (isDragTarget) {
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 3;
    } else if (isBeingDragged) {
      ctx.strokeStyle = '#4ecca3';
      ctx.lineWidth = 3;
    } else if (isDefaultSave) {
      ctx.strokeStyle = '#ffc107';
      ctx.lineWidth = 2;
    } else {
      ctx.strokeStyle = isSelected ? '#4ecca3' : '#555';
      ctx.lineWidth = isSelected ? 2 : 1;
    }
    ctx.strokeRect(x, y, width, this.slotHeight - 5);

    if (save.exists) {
      // Default save - special rendering
      if (isDefaultSave) {
        // Title with star icon
        ctx.fillStyle = '#ffc107';
        ctx.font = 'bold 14px monospace';
        ctx.textAlign = 'left';
        ctx.fillText('\u2605 ' + save.name, x + 10, y + 18);

        // Info text
        ctx.fillStyle = '#ddd';
        ctx.font = '11px monospace';
        ctx.fillText(`Lvl 10 | ${t('save_day')} ${save.day} | Full base & all items`, x + 10, y + 32);

        // Only show LOAD button in load mode
        const buttonY = y + 38;
        if (this.mode === 'load') {
          ctx.fillStyle = '#ffc107';
          ctx.fillRect(x, buttonY, 50, 18);
          ctx.fillStyle = '#000';
          ctx.font = 'bold 10px monospace';
          ctx.textAlign = 'center';
          ctx.fillText(t('save_btn_load'), x + 25, buttonY + 13);

          // Info text about this being a demo
          ctx.fillStyle = '#888';
          ctx.font = '10px monospace';
          ctx.textAlign = 'left';
          ctx.fillText(t('save_demo_info'), x + 60, buttonY + 13);
        } else {
          // In save mode, show info that this slot is read-only
          ctx.fillStyle = '#888';
          ctx.font = '10px monospace';
          ctx.textAlign = 'left';
          ctx.fillText(t('save_demo_readonly'), x + 10, buttonY + 13);
        }
        return;
      }

      // Save name (or edit field)
      if (this.editingName && this.editingSlot === save.slot) {
        // Editing mode
        ctx.fillStyle = '#222';
        ctx.fillRect(x + 5, y + 5, width - 10, 22);
        ctx.strokeStyle = this.isSaveAsMode ? '#3498db' : '#f39c12';
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 5, y + 5, width - 10, 22);

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(this.editBuffer + '_', x + 10, y + 20);

        // Show hint
        ctx.fillStyle = '#888';
        ctx.font = '10px monospace';
        ctx.textAlign = 'right';
        const hint = this.isSaveAsMode ? t('save_hint_save') : t('save_hint_rename');
        ctx.fillText(hint, x + width - 10, y + 18);
      } else {
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(save.name, x + 10, y + 18);
      }

      // Save info
      const date = new Date(save.timestamp);
      const dateStr = date.toLocaleDateString();
      const playTimeHours = Math.floor(save.playTime / 3600);
      const playTimeMins = Math.floor((save.playTime % 3600) / 60);

      ctx.fillStyle = '#aaa';
      ctx.font = '11px monospace';
      ctx.fillText(`${t('save_day')} ${save.day} | ${playTimeHours}h ${playTimeMins}m | ${dateStr}`, x + 10, y + 32);

      // Buttons
      const buttonY = y + 38;

      // Load/Save button
      ctx.fillStyle = this.mode === 'load' ? '#4ecca3' : '#3498db';
      ctx.fillRect(x, buttonY, 50, 18);
      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(this.mode === 'load' ? t('save_btn_load') : t('save_btn_save'), x + 25, buttonY + 13);

      // Rename button
      ctx.fillStyle = '#f39c12';
      ctx.fillRect(x + 55, buttonY, 55, 18);
      ctx.fillStyle = '#000';
      ctx.fillText(t('save_btn_rename'), x + 82, buttonY + 13);

      // Export button
      ctx.fillStyle = '#9b59b6';
      ctx.fillRect(x + 115, buttonY, 50, 18);
      ctx.fillStyle = '#fff';
      ctx.fillText(t('save_btn_export'), x + 140, buttonY + 13);

      // Delete button
      ctx.fillStyle = '#e74c3c';
      ctx.fillRect(x + 170, buttonY, 50, 18);
      ctx.fillStyle = '#fff';
      ctx.fillText(t('save_btn_delete'), x + 195, buttonY + 13);
    } else {
      // Empty slot - check if editing this slot
      if (this.editingName && this.editingSlot === save.slot) {
        // Editing mode for new save
        ctx.fillStyle = '#222';
        ctx.fillRect(x + 5, y + 5, width - 10, 22);
        ctx.strokeStyle = '#3498db';
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 5, y + 5, width - 10, 22);

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(this.editBuffer + '_', x + 10, y + 20);

        // Show hint
        ctx.fillStyle = '#888';
        ctx.font = '10px monospace';
        ctx.textAlign = 'right';
        ctx.fillText(t('save_hint_save'), x + width - 10, y + 18);

        // Info text
        ctx.fillStyle = '#aaa';
        ctx.font = '11px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(t('save_new_save_hint'), x + 10, y + 40);
      } else {
        ctx.fillStyle = '#666';
        ctx.font = '14px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(t('save_empty'), x + 10, y + 25);

        const buttonY = y + 38;

        if (this.mode === 'save') {
          // New save button
          ctx.fillStyle = '#4ecca3';
          ctx.fillRect(x, buttonY, 80, 18);
          ctx.fillStyle = '#000';
          ctx.font = 'bold 10px monospace';
          ctx.textAlign = 'center';
          ctx.fillText(t('save_btn_new_save'), x + 40, buttonY + 13);
        }

        // Import button (always available for empty slots)
        ctx.fillStyle = '#9b59b6';
        ctx.fillRect(x + 90, buttonY, 60, 18);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(t('save_btn_import'), x + 120, buttonY + 13);
      }
    }
  }

  /** Handle scroll wheel */
  handleScroll(deltaY: number): void {
    if (!this.isVisible) return;

    const maxScroll = Math.max(0, (this.saves.length - this.maxVisibleSlots) * this.slotHeight);
    this.scrollOffset = Math.max(0, Math.min(maxScroll, this.scrollOffset + deltaY));
  }

  isEditingName(): boolean {
    return this.editingName;
  }
}
